import { API_HOST } from './constants/paths';

const normalizeBoolean = (value, defaultValue = false) => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;

    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'si', 'on'].includes(normalizedValue)) return true;
        if (['false', '0', 'no', 'off', ''].includes(normalizedValue)) return false;
    }

    return Boolean(value);
};

const normalizeCarrierKey = (value) => value?.toString?.().trim().toLowerCase();
const API_BASE_HOST = API_HOST.replace(/\/api\/v1\/?$/, '');

export const resolveDropoffCarrierIconPath = (iconPath) => {
    const normalizedPath = iconPath?.toString?.().trim();
    if (!normalizedPath) return null;
    if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

    // ActiveStorage paths come from backend API host.
    if (normalizedPath.startsWith('/rails/active_storage')) {
        return `${API_BASE_HOST}${normalizedPath}`;
    }

    return normalizedPath;
};

export const DEFAULT_DROPOFF_CARRIER_CATALOG = Object.freeze([
    {
        key: 'mercadolibre',
        label: 'MercadoLibre',
        iconPath: '/images/icons/mercado-libre-color.svg',
        supportsShippingPoints: true,
    },
    {
        key: 'starken',
        label: 'Starken',
        iconPath: '/images/icons/starken2.svg',
        supportsShippingPoints: true,
    },
    {
        key: 'bluexpress',
        label: 'Bluexpress',
        iconPath: '/images/icons/blueexpress.svg',
        supportsShippingPoints: true,
    },
    {
        key: 'chilexpress',
        label: 'Chilexpress',
        iconPath: '/images/icons/chilexpress.svg',
        supportsShippingPoints: true,
    },
]);

export const DEFAULT_DROPOFF_CARRIER_KEYS = Object.freeze(
    DEFAULT_DROPOFF_CARRIER_CATALOG.map((carrier) => carrier.key)
);

const DEFAULT_DROPOFF_CARRIER_BY_KEY = DEFAULT_DROPOFF_CARRIER_CATALOG.reduce((acc, carrier) => {
    acc[carrier.key] = carrier;
    return acc;
}, {});

// El backend ya manda la dirección formateada en `text`: armarla acá desde
// short_address + short_zone dejaba " , , RM" cuando HERE resolvía a nivel
// ciudad y esos campos venían con espacios y comas sueltas.
const normalizeDropoffDefaultAddress = (address) => {
    if (!address) return null;

    const text = (address.text ?? '').trim();
    if (!text) return null;

    return {
        locationId: address.locationId ?? address.location_id ?? null,
        text,
        lat: address.lat ?? null,
        lng: address.lng ?? null,
    };
};

const normalizeDropoffCarrier = (carrier = {}) => {
    const key = normalizeCarrierKey(carrier?.key);
    if (!key) return null;

    const defaultCarrier = DEFAULT_DROPOFF_CARRIER_BY_KEY[key] || {};
    return {
        id: carrier?.id || null,
        key,
        label: carrier?.label || defaultCarrier.label || key,
        iconPath: resolveDropoffCarrierIconPath(
            carrier?.iconPath || carrier?.icon_path || defaultCarrier.iconPath || null
        ),
        supportsShippingPoints: normalizeBoolean(
            carrier?.supportsShippingPoints ?? carrier?.supports_shipping_points,
            defaultCarrier?.supportsShippingPoints ?? false
        ),
        custom: normalizeBoolean(carrier?.custom, false),
        defaultAddress: normalizeDropoffDefaultAddress(
            carrier?.defaultAddress || carrier?.default_address
        ),
    };
};

export const normalizeDropoffCarrierCatalog = (catalog) => {
    if (!Array.isArray(catalog) || catalog.length === 0) {
        return [...DEFAULT_DROPOFF_CARRIER_CATALOG];
    }

    const normalizedCatalog = [];
    const seenKeys = new Set();

    catalog.forEach((carrier) => {
        const normalizedCarrier = normalizeDropoffCarrier(carrier);
        if (!normalizedCarrier || seenKeys.has(normalizedCarrier.key)) return;

        seenKeys.add(normalizedCarrier.key);
        normalizedCatalog.push(normalizedCarrier);
    });

    return normalizedCatalog.length > 0
        ? normalizedCatalog
        : [...DEFAULT_DROPOFF_CARRIER_CATALOG];
};

export const normalizeDropoffCarrierKeys = (
    keys,
    catalog = DEFAULT_DROPOFF_CARRIER_CATALOG,
    fallbackKeys = DEFAULT_DROPOFF_CARRIER_KEYS
) => {
    const normalizedCatalog = normalizeDropoffCarrierCatalog(catalog);
    const validKeys = new Set(normalizedCatalog.map((carrier) => carrier.key));

    const normalizedKeys = Array.isArray(keys)
        ? keys.map(normalizeCarrierKey).filter(Boolean)
        : [];

    const uniqueValidKeys = normalizedKeys.filter((key, index) => (
        validKeys.has(key) && normalizedKeys.indexOf(key) === index
    ));

    if (uniqueValidKeys.length > 0) {
        return uniqueValidKeys;
    }

    const fallbackValidKeys = fallbackKeys
        .map(normalizeCarrierKey)
        .filter((key, index, array) => (
            Boolean(key) && validKeys.has(key) && array.indexOf(key) === index
        ));

    return fallbackValidKeys.length > 0
        ? fallbackValidKeys
        : normalizedCatalog.map((carrier) => carrier.key);
};

export const getDropoffCarrierByKey = (key, catalog = DEFAULT_DROPOFF_CARRIER_CATALOG) => {
    const normalizedKey = normalizeCarrierKey(key);
    if (!normalizedKey) return null;

    const normalizedCatalog = normalizeDropoffCarrierCatalog(catalog);
    return normalizedCatalog.find((carrier) => carrier.key === normalizedKey) || null;
};

// Nombre y logo de quien despacha un pedido. Busca primero en el catalogo de la empresa y
// despues en el de por defecto; un transportista que no esta en ninguno (los propios del
// ERP, como "alpha_group") se muestra con su clave legible y sin logo.
export const getDropoffCarrierDisplay = (key, catalog) => {
    const normalizedKey = normalizeCarrierKey(key);
    if (!normalizedKey) return null;

    const carrier = getDropoffCarrierByKey(normalizedKey, catalog) || getDropoffCarrierByKey(normalizedKey);
    if (carrier) return { key: normalizedKey, label: carrier.label, iconPath: carrier.iconPath };

    const label = normalizedKey
        .split(/[_\-\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return { key: normalizedKey, label, iconPath: null };
};

export const getDropoffCarrierOptions = (catalog = DEFAULT_DROPOFF_CARRIER_CATALOG) =>
    normalizeDropoffCarrierCatalog(catalog).map((carrier) => ({
        value: carrier.key,
        label: carrier.label,
    }));
